/**
 * 多维度明细表组件
 */
import React, { useMemo, useState, useCallback, useContext } from 'react';
import { Bubble, Checkbox } from 'tea-component';
import type { SortBy } from 'tea-component/lib/table/addons';
import CardCommon from '@op/components/CardCommon';
import TableCommonHook from '@op/components/TableCommonHook';
import useLanguage from '@op/hooks/useLanguage';
import useDetailTableData from '../../hooks/useDetailTableData';
import { DBO_V, DBO_U, DBO_TRANS } from '@op/utils/format';
import ChannelLabel from '@op/components/ChannelLabel';
import type { PageInfoType } from '@op/types/common';
import { DIMENSION_TYPES, DIMENSION_OPTIONS } from '../../constants';
import './index.scss';
import { usePageInfo } from '@op/hooks/common';
import SvgIcon from '@home/components/SvgIcon';
import { SecurityWeeklyReportContext } from '../../context';
import TextBubbles from '@home/components/TextBubbles';
import moment from 'moment';

interface IDetailTableProps {
  filter: any;
}

// 条形占比可视化组件
const RatioBar: React.FC<{ value: number }> = ({ value }) => {
  // 将小数转换为百分比
  const percentage = value;
  const absPercentage = Math.abs(percentage);
  // 限制最大显示比例为 100%
  const displayPercentage = Math.min(absPercentage, 100);

  const isPositive = value >= 0;

  return (
    <div className="dbo-ratio-bar">
      {/* 数值条 */}
      <div
        className={`dbo-ratio-bar__fill ${isPositive
          ? 'dbo-ratio-bar__fill--positive'
          : 'dbo-ratio-bar__fill--negative'
          }`}
        style={{ width: `${displayPercentage / 2}%` }}
      />
      {/* 数值文本 - 放在进度条上右对齐 */}
      <span className="dbo-ratio-bar__value">{DBO_V.ratio(percentage)}</span>
    </div>
  );
};

const DetailTable: React.FC<IDetailTableProps> = ({ filter }) => {
  const { formatMessage, isEn } = useLanguage();
  const { toFeeds } = useContext(SecurityWeeklyReportContext);

  // 维度选择状态
  const [selectedDimensions, setSelectedDimensions] = useState<string[]>([]);

  // 分页状态
  const { pageInfo, changePageInfo, resetPageInfo } = usePageInfo({
    defaultPageInfo: { page: 1, page_size: 10 },
  });

  // 排序状态
  const [sortBy, setSortBy] = useState<SortBy<any>>({
    by: 'current_volume',
    order: 'desc',
  });

  // 适配器函数
  const handlePageInfoChange = useCallback((info: PageInfoType) => {
    changePageInfo(info);
  }, []);

  const handleSortChange = useCallback((sort: SortBy<any>) => {
    setSortBy(sort);
    resetPageInfo();
  }, []);

  // 请求参数
  const requestParams = useMemo(
    () => ({
      ...filter,
      page: pageInfo.page,
      page_size: pageInfo.page_size,
      sort_by: sortBy.by,
      sort_order: sortBy.order,
      dimensions:
        selectedDimensions.length === 0
          ? []
          : [...selectedDimensions, 'category'],
    }),
    [filter, pageInfo],
  );

  const { data, total, summary, loading, empty } =
    useDetailTableData(requestParams);

  // 计算最高值项
  const highestValueItem = useMemo(() => {
    if (data.length === 0) return null;
    return data.reduce(
      (max, item) => (item.current_volume > max.current_volume ? item : max),
      data[0],
    );
  }, [data]);

  // 最高值描述文案
  const highestValueText = useMemo(() => {
    if (!highestValueItem) return '';
    const typeName = filter.subcategory
      ? `${highestValueItem.category} - ${highestValueItem.subcategory}`
      : highestValueItem.category;
    return `${formatMessage({
      id: 'Highest Mentions',
    })}: ${typeName} (${DBO_V.reviews(highestValueItem.current_volume)})`;
  }, [highestValueItem, formatMessage, filter]);

  // 判断是否跨周（开始时间和结束时间不在同一周内）
  const isCrossWeek = useMemo(() => {
    const startTime = moment(filter?.start_time);
    const endTime = moment(filter?.end_time);
    // 获取开始时间所在周的周日
    const weekEnd = moment(startTime).endOf('week');
    // 如果结束时间超过了开始时间所在周的周日，则跨周
    return endTime.isAfter(weekEnd) || filter?.date_type === 'daily';
  }, [filter]);

  // 表格列定义
  const columns = useMemo(() => {
    const baseColumns: any[] = [
      {
        key: 'category',
        header: formatMessage({ id: 'Dimension' }),
        render: (record: any) => {
          const displayName = isEn ? record.category_en : record.category;

          return (
            <div className="dbo-name-box">
              {displayName}
              <Bubble
                arrowPointAtCenter
                content={formatMessage('View Comment Details')}
              >
                <div
                  className="dbo-view-detail-Icon"
                  onClick={() =>
                    toFeeds(record?.category_en)
                  }
                >
                  <SvgIcon type="view-details-icon" />
                </div>
              </Bubble>
            </div>
          );
        },
        width: 200
      },
    ];

    selectedDimensions.forEach((dimension) => {
      const option = DIMENSION_OPTIONS.find((opt) => opt.value === dimension);
      if (!option) return;

      const column: any = {
        key: option.columnKey,
        header: formatMessage({ id: option.columnLabel }),
        render: (record: any) => {
          if (option.columnKey === 'subcategory') {
            const value = isEn ? record.subcategory_en : record.subcategory;
            return (
              <TextBubbles text={value} />
            );
          }
          const value = record[option.columnKey];
          if (!value) return '-';

          // 特殊处理语言和渠道
          if (option.value === DIMENSION_TYPES.language) {
            return DBO_TRANS.language(value);
          }
          if (option.value === DIMENSION_TYPES.channel) {
            return <ChannelLabel channel_name={value} showText={true} />;
          }
          return value;
        },
        width: option.width,
      };

      // date 列用 unshift 排到第一位，其他列用 push 追加
      if (option.value === DIMENSION_TYPES.date) {
        baseColumns.unshift(column);
      } else {
        baseColumns.push(column);
      }
    });

    // 合并所有列：维度列 → category列 → 数值列
    const metricColumns = [
      {
        key: 'current_volume',
        align: 'right',
        header: formatMessage({ id: isCrossWeek ? 'Current Period Mentions-1' : 'Current Period Mentions' }),
        render: (record: any) => DBO_V.reviews(record.current_volume),
        width: 200,
      },
      {
        key: 'previous_volume',
        align: 'right',
        header: formatMessage({ id: isCrossWeek ? 'Previous Period Mentions-1' : 'Previous Period Mentions' }),
        render: (record: any) => DBO_V.reviews(record.previous_volume),
        width: 200,
      },
      {
        key: 'change_rate',
        align: 'right',
        header: formatMessage({ id: 'Mentions(change%)' }),
        render: (record: any) => <RatioBar value={record.change_rate} />,
        width: 200,
      },
      {
        key: 'contribution_rate',
        align: 'right',
        header: formatMessage({ id: 'Contribution to Change (%)' }),
        render: (record: any) => <RatioBar value={record.contribution_rate} />,
        width: 200,
      },
    ];

    return [...baseColumns, ...metricColumns];
  }, [formatMessage, selectedDimensions, highestValueItem]);

  // 维度选择变更
  const handleDimensionChange = useCallback((dimension: string) => {
    setSelectedDimensions((prev) => {
      if (prev.includes(dimension)) {
        return prev.filter((d) => d !== dimension);
      }
      return [...prev, dimension];
    });
    // 重置分页
    resetPageInfo();
  }, []);

  // 下载
  const handleDownload = () => {
    DBO_U.download({
      key: 'WeeklyReportDetailTable',
      fileName: 'Weekly Report - Detail Table',
      filter: requestParams,
      auxiliaryParams: {
        dimensions: selectedDimensions,
      },
    });
  };

  return (
    <CardCommon
      className="dbo-detail-table"
      title={formatMessage({ id: 'Change Breakdown by Dimension' })}
      subTitle={highestValueText}
      loadingHeight={400}
      hasDownload={!empty}
      handleDownloadOnClick={handleDownload}
      logBtnId="WR_DetailTable"
    >
      {/* 维度选择器 */}
      <div className="dbo-detail-table__dimensions">
        <span className="dbo-detail-table__dimensions-label">
          {formatMessage({ id: 'Dimension' })}:
        </span>
        {DIMENSION_OPTIONS.map((option) => (
          <Checkbox
            key={option.value}
            value={selectedDimensions.includes(option.value)}
            onChange={() => handleDimensionChange(option.value)}
          >
            {formatMessage({ id: option.checkboxLabel })}
          </Checkbox>
        ))}
      </div>

      {/* 声量变化摘要 */}
      {summary?.top_fluctuation && (
        <div className="dbo-detail-table__summary">
          {(() => {
            const top = summary.top_fluctuation;
            const name = isEn ? top.category_en : top.category;
            const changeRateValue = DBO_V.ratio(top.change_rate, '-', 2);
            const changeRateText =
              top.change_rate > 0 && changeRateValue !== '-'
                ? `+${changeRateValue}`
                : changeRateValue;
            const contributionRateText = DBO_V.ratio(
              top.contribution_rate,
              '-',
              2,
            );
            return formatMessage(
              {
                id: 'Largest change contributor this week: {B} {A}. {B} {A}: {C}  change ({D} contribution).',
              },
              {
                A: name,
                B: '',
                C: changeRateText,
                D: contributionRateText,
              },
            );
          })()}
        </div>
      )}

      {/* 表格 */}
      <TableCommonHook
        records={data}
        columns={columns}
        total={total}
        bordered="all"
        pageInfo={pageInfo}
        setPageInfo={handlePageInfoChange}
        sortColumns={[
          "date",
          'current_volume',
          'previous_volume',
          'change_rate',
          'contribution_rate',
        ]}
        sortInfo={[sortBy, handleSortChange]}
        defaultSort={{ by: 'current_volume', order: 'desc' }}
        tableSticky
        loading={loading}
        height={500}
        minWidth={"auto"}
      />
    </CardCommon>
  );
};

export default DetailTable;
